import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Download,
  Timer,
  ClipboardList,
  Clock,
  DollarSign
} from 'lucide-react'
import DataTable from '../../components/DataTable/DataTable'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import timeEntryService from '../../services/timeEntryService'
import '../../components/ConfirmationModal/ConfirmationModal.css'
// Consolidated CSS is imported globally

const TimeEntry = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalEntries: 0,
    totalHours: 0,
    totalBillableHours: 0,
    totalBillingAmount: 0,
    draftEntries: 0,
    submittedEntries: 0,
    approvedEntries: 0,
    rejectedEntries: 0
  })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [entriesResponse, statsResponse] = await Promise.all([
        timeEntryService.getAllTimeEntries(),
        timeEntryService.getTimeEntryStats()
      ])
      
      if (entriesResponse.success) {
        // Transform _id to id for frontend compatibility
        const transformedData = (entriesResponse.data || []).map(entry => ({
          ...entry,
          id: entry._id
        }))
        setData(transformedData)
        // Only show toast if data is empty and we haven't shown it before
        if (entriesResponse.data?.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No time entry records found. Create your first time entry!')
        }
      } else {
        toast.error(entriesResponse.message || 'Failed to load time entries')
      }
      
      if (statsResponse.success) {
        setStats(statsResponse.data)
      } else {
        toast.error(statsResponse.message || 'Failed to load statistics')
      }
    } catch (error) {
      console.error('Error loading time entry records:', error)
      if (error.response?.status === 401) {
        toast.error('Authentication required. Please log in again.')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to view time entries')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to load time entry records')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const formatHours = (totalMinutes) => {
    if (!totalMinutes) return '0h 0m'
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return `${hours}h ${minutes}m`
  }

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount || 0)
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'Draft': 'inactive',
      'Submitted': 'pending',
      'Approved': 'active',
      'Rejected': 'danger',
      'Invoiced': 'active'
    }
    return statusMap[status] || 'inactive'
  }

  const columns = [
    {
      accessorKey: 'entryNumber',
      header: 'Entry Number',
      cell: ({ getValue }) => (
        <div className="service-code-cell" style={{ textAlign: 'left' }}>
          <span className="code-text">{getValue()}</span>
        </div>
      ),
    },
    {
      accessorKey: 'employeeName',
      header: 'Employee',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.employeeCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.employeeCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'projectName',
      header: 'Project',
      cell: ({ getValue, row }) => (
        <div className="service-description-cell" style={{ textAlign: 'left' }}>
          <span className="description-text">{getValue() || '-'}</span>
          {row.original.projectCode && (
            <div style={{ fontSize: '12px', color: 'var(--color-text-secondary, #5B6478)' }}>{row.original.projectCode}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'entryDate',
      header: 'Date',
      cell: ({ getValue }) => {
        const date = getValue()
        return (
          <div style={{ textAlign: 'center' }}>
            {date ? new Date(date).toLocaleDateString() : '-'}
          </div>
        )
      },
    },
    {
      accessorKey: 'totalMinutes',
      header: 'Hours',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center', fontWeight: '600' }}>
          {formatHours(getValue())}
        </div>
      ),
    },
    {
      accessorKey: 'activityType',
      header: 'Activity',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          {getValue() || '-'}
        </div>
      ),
    },
    {
      accessorKey: 'isBillable',
      header: 'Billable',
      cell: ({ getValue }) => (
        <div style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getValue() ? 'active' : 'inactive'}`}>
            {getValue() ? 'Yes' : 'No'}
          </span>
        </div>
      ),
    },
    {
      accessorKey: 'billingAmount',
      header: 'Billing Amount',
      cell: ({ getValue, row }) => (
        <div style={{ textAlign: 'right', fontWeight: '600' }}>
          {row.original.isBillable ? formatCurrency(getValue(), row.original.currency || 'INR') : '-'}
        </div>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => (
        <div className="active-cell" style={{ textAlign: 'center' }}>
          <span className={`status-badge ${getStatusBadgeClass(getValue())}`}>
            {getValue() || '-'}
          </span>
        </div>
      ),
    },
  ]

  const handleAdd = () => {
    navigate('/timesheets/time-entry/form')
  }

  const handleEdit = (entryData) => {
    navigate('/timesheets/time-entry/edit', { state: { entryData } })
  }

  const handleView = (entryData) => {
    navigate('/timesheets/time-entry/view', { state: { entryData } })
  }

  const handleDelete = (entryData) => {
    setDeleteItem(entryData)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      toast.info('Deleting time entry...')
      const response = await timeEntryService.deleteTimeEntry(deleteItem._id)
      if (response.success) {
        toast.success('Time entry deleted successfully!', { autoClose: 2000 })
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete time entry')
      }
    } catch (error) {
      console.error('Error deleting time entry:', error)
      if (error.response?.status === 404) {
        toast.error('Time entry not found')
      } else if (error.response?.status === 403) {
        toast.error('You do not have permission to delete this time entry')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to delete time entry. Please try again.')
        }
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const cancelDelete = () => {
    setShowDeleteModal(false)
    setDeleteItem(null)
  }

  const handleExport = () => {
    toast.info('Export functionality coming soon!')
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb
        items={[
          { label: 'Home', path: '/' },
          { label: 'Timesheets', path: '/timesheets' },
          { label: 'Time Entries', isCurrent: true }
        ]}
      />

      <div className="page-header">
        <div className="page-title">
          <h1>Time Entries</h1>
          <p>Daily time logging and project tracking</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Entry
          </button>
          <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleExport}>
            <Download size={16} />
            Export
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card deals">
          <div className="metric-header">
            <div className="metric-icon">
              <ClipboardList size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalEntries || 0}</div>
          <div className="metric-title">Total Entries</div>
          <div className="metric-subtitle">All time entries</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Timer size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Hours</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalHours ? stats.totalHours.toFixed(1) : '0.0'}h</div>
          <div className="metric-title">Total Hours</div>
          <div className="metric-subtitle">Logged time</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Billable</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalBillableHours ? stats.totalBillableHours.toFixed(1) : '0.0'}h</div>
          <div className="metric-title">Billable Hours</div>
          <div className="metric-subtitle">Client billable time</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Amount</span>
            </div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalBillingAmount)}</div>
          <div className="metric-title">Billing Amount</div>
          <div className="metric-subtitle">Total billing value</div>
        </div>
      </div>

      <div className="data-table-section">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          enableSelection={false}
          noCard={true}
          pageSize={10}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={cancelDelete}
        onConfirm={confirmDelete}
        title="Delete Time Entry"
        message={`Are you sure you want to delete time entry "${deleteItem?.entryNumber}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default TimeEntry

